import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { useProfile, useUpdateProfile } from '@/api/hooks/useProfile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';

const HEALTH_GOALS = [
  { id: 'optimize_testosterone', label: 'Improve Testosterone' },
  { id: 'lose_fat', label: 'Lose Fat' },
  { id: 'build_muscle', label: 'Build Muscle' },
  { id: 'improve_sleep', label: 'Improve Sleep' },
  { id: 'reduce_inflammation', label: 'Reduce Inflammation' },
  { id: 'improve_energy', label: 'Improve Energy' },
  { id: 'longevity', label: 'Longevity' },
  { id: 'sexual_health', label: 'Sexual Health' },
  { id: 'mental_clarity', label: 'Mental Clarity' },
];

const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentary' },
  { value: 'lightly_active', label: 'Lightly Active (1-2 days/week)' },
  { value: 'moderately_active', label: 'Moderately Active (3-4 days/week)' },
  { value: 'very_active', label: 'Very Active (5-6 days/week)' },
  { value: 'elite', label: 'Elite/Athlete (daily+)' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

function heightDisplay(inches?: number): string {
  if (!inches) return '';
  const ft = Math.floor(inches / 12);
  const remaining = inches % 12;
  return `${ft}'${remaining}"`;
}

export function ProfilePage() {
  const { data, isLoading } = useProfile();
  const update = useUpdateProfile();
  const [showSaved, setShowSaved] = useState(false);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    sex: '',
    height_inches: '',
    weight_lbs: '',
    activity_level: '',
    health_goals: [] as string[],
    current_supplements: '',
    medical_notes: '',
    state: '',
  });

  useEffect(() => {
    if (data?.profile) {
      const p = data.profile as unknown as Record<string, unknown>;
      setForm({
        first_name: (p.first_name as string) || '',
        last_name: (p.last_name as string) || '',
        date_of_birth: (p.date_of_birth as string) || '',
        sex: (p.sex as string) || '',
        height_inches: p.height_inches ? String(p.height_inches) : '',
        weight_lbs: p.weight_lbs ? String(p.weight_lbs) : '',
        activity_level: (p.activity_level as string) || '',
        health_goals: (p.health_goals as string[]) || [],
        current_supplements: (p.current_supplements as string) || '',
        medical_notes: (p.medical_notes as string) || '',
        state: (p.state as string) || '',
      });
    }
  }, [data]);

  const toggleGoal = (goalId: string) => {
    setForm((prev) => ({
      ...prev,
      health_goals: prev.health_goals.includes(goalId)
        ? prev.health_goals.filter((g) => g !== goalId)
        : [...prev.health_goals, goalId],
    }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: Record<string, unknown> = {
        display_name: [form.first_name, form.last_name].filter(Boolean).join(' '),
        first_name: form.first_name,
        last_name: form.last_name,
        date_of_birth: form.date_of_birth || undefined,
        sex: form.sex || undefined,
        height_inches: form.height_inches ? Number(form.height_inches) : undefined,
        weight_lbs: form.weight_lbs ? Number(form.weight_lbs) : undefined,
        activity_level: form.activity_level || undefined,
        health_goals: form.health_goals,
        current_supplements: form.current_supplements || undefined,
        medical_notes: form.medical_notes || undefined,
        state: form.state || undefined,
      };
      await update.mutateAsync(payload);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 3000);
    } catch {
      // error on update.error
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-copper border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-navy">Profile</h1>
        <p className="mt-1 text-gray-500">
          Your health profile helps us provide more accurate analysis
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Personal */}
        <Card>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={form.first_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, first_name: e.target.value }))
                  }
                  placeholder="John"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={form.last_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, last_name: e.target.value }))
                  }
                  placeholder="Doe"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dob">Date of Birth</Label>
                <Input
                  id="dob"
                  type="date"
                  value={form.date_of_birth}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, date_of_birth: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sex">Biological Sex</Label>
                <Select
                  value={form.sex}
                  onValueChange={(v) => setForm((f) => ({ ...f, sex: v }))}
                >
                  <SelectTrigger id="sex">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Body */}
        <Card>
          <CardHeader>
            <CardTitle>Body Measurements</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="height">Height (inches)</Label>
                <div className="relative">
                  <Input
                    id="height"
                    type="number"
                    value={form.height_inches}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, height_inches: e.target.value }))
                    }
                    placeholder="72"
                    min={48}
                    max={96}
                  />
                  {form.height_inches && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                      {heightDisplay(Number(form.height_inches))}
                    </span>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="weight">Weight (lbs)</Label>
                <Input
                  id="weight"
                  type="number"
                  value={form.weight_lbs}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, weight_lbs: e.target.value }))
                  }
                  placeholder="185"
                  min={80}
                  max={500}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="activity">Activity Level</Label>
                <Select
                  value={form.activity_level}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, activity_level: v }))
                  }
                >
                  <SelectTrigger id="activity">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIVITY_LEVELS.map((lvl) => (
                      <SelectItem key={lvl.value} value={lvl.value}>
                        {lvl.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Health Goals */}
        <Card>
          <CardHeader>
            <CardTitle>Health Goals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {HEALTH_GOALS.map((goal) => {
                const selected = form.health_goals.includes(goal.id);
                return (
                  <button
                    key={goal.id}
                    type="button"
                    onClick={() => toggleGoal(goal.id)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors border ${
                      selected
                        ? 'bg-copper text-white border-copper'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-copper/50'
                    }`}
                  >
                    {goal.label}
                  </button>
                );
              })}
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="supplements">Current Supplements</Label>
              <Textarea
                id="supplements"
                value={form.current_supplements}
                onChange={(e) =>
                  setForm((f) => ({ ...f, current_supplements: e.target.value }))
                }
                placeholder="e.g., Vitamin D 5000 IU, Magnesium Glycinate 400mg, Fish Oil 2g"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="medical_notes">Medical Notes</Label>
              <Textarea
                id="medical_notes"
                value={form.medical_notes}
                onChange={(e) =>
                  setForm((f) => ({ ...f, medical_notes: e.target.value }))
                }
                placeholder="Any relevant medical conditions, allergies, or medications"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Location */}
        <Card>
          <CardHeader>
            <CardTitle>Location</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-w-xs">
              <Label htmlFor="state">State</Label>
              <Select
                value={form.state}
                onValueChange={(v) => setForm((f) => ({ ...f, state: v }))}
              >
                <SelectTrigger id="state">
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Error */}
        {update.error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {update.error.message}
          </div>
        )}

        {/* Save */}
        <div className="flex items-center gap-4">
          <Button type="submit" size="lg" disabled={update.isPending}>
            {update.isPending ? 'Saving...' : 'Save Changes'}
          </Button>

          {showSaved && (
            <motion.span
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-1.5 text-sm text-emerald-600"
            >
              <CheckCircle className="h-4 w-4" />
              Changes saved
            </motion.span>
          )}
        </div>
      </form>
    </div>
  );
}
